import { useEffect, useState } from 'react';

/**
 * A `useState` whose value is mirrored to `sessionStorage`, so it survives
 * component remounts and full-page reloads within the same tab session. The
 * initial value is, in precedence order: `seed` (when provided), then the stored
 * value, then `defaultValue`; every change is written back under `key`.
 *
 * `seed` lets a one-shot external source (e.g. URL query params from a link
 * hand-off) take precedence over the persisted value on first mount, so the
 * very first render already reflects it — no post-mount effect that would make
 * the first render use a stale value. It is read once (in the lazy initializer);
 * later changes to `seed` are ignored.
 *
 * Use this for declarative filter/query state read straight off the returned
 * value — unlike `components/filters/useFilterState`, which is built around a
 * debounced `onFilterChange` callback for imperative-refetch patterns.
 *
 * sessionStorage access is wrapped in try/catch so a disabled or over-quota
 * store (private browsing, embedded webviews) degrades to plain in-memory state.
 */
export function useSessionStorageState<T>(key: string, defaultValue: T, seed?: T) {
  const [value, setValue] = useState<T>(() => {
    if (seed !== undefined) return seed;
    try {
      const raw = sessionStorage.getItem(key);
      return raw === null ? defaultValue : (JSON.parse(raw) as T);
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // sessionStorage unavailable (private mode / quota) — keep state in memory only.
    }
  }, [key, value]);

  return [value, setValue] as const;
}
